'use client';

import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, type LucideIcon } from 'lucide-react';

import { useTranslation } from '../../lib/i18n';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { consumeHorizontalWheel, horizontalEdgeFadeStyle, revealHorizontalItem, useHorizontalOverflow } from './horizontalScroll';
import { RadioGroup, RadioGroupItem } from './shadcn/radio-group';

export interface SegmentedOption {
  value: string;
  label: string;
  icon?: LucideIcon;
  /** A pre-rendered decorative brand mark, for catalogs whose icons are not Lucide components. */
  iconContent?: ReactNode;
  /** Unavailable choices stay visible but Radix excludes them from selection and roving focus. */
  disabled?: boolean;
  /** How many records the option leads to. Rendered as a quiet suffix and folded into the accessible
   *  name, so a section that carries a figure keeps it when the control is used as a page's section
   *  navigation — the presentation this replaced showed one and dropping it would lose information. */
  count?: number;
  /** Host-only: show the icon without the label. The label stays the option's accessible name and becomes
   *  its tooltip, for a choice whose marks say it better than words (the Elowen CLI installer's systems). */
  iconOnly?: boolean;
}

/** Labels retain their intrinsic width; the track scrolls instead of truncating a choice. */
const LABEL_CLASS = 'shrink-0 whitespace-nowrap';

/** The overflow arrow of a `nowrap` track, shown on the edge that hides content. A fine pointer has no finger to
 *  swipe with, so it gets a real button; a touch pointer swipes natively, so the button is CSS-hidden there. It
 *  sits over the track's edge rather than beside it, so the track keeps its width and its edge fade. */
function EdgeArrow({ direction, onClick }: { direction: 'left' | 'right'; onClick: () => void }) {
  // Read here rather than in Segmented: only an overflowing track draws an arrow, so a track that fits works
  // wherever no language provider is mounted.
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      icon={direction === 'left' ? ChevronLeft : ChevronRight}
      aria-label={direction === 'left' ? t.common.scrollLeft : t.common.scrollRight}
      onClick={onClick}
      className={cn('absolute top-1/2 z-[var(--z-lift)] -translate-y-1/2 bg-control pointer-coarse:hidden', direction === 'left' ? 'left-0.5' : 'right-0.5')}
    />
  );
}

/** A connected single-choice control for modes, filters, types, priorities and section views.
 *
 *  This is a Radix RadioGroup rather than a ToggleGroup because one value is always selected and clicking
 *  it again must not turn the set off. That gives the control its native `radiogroup` / `radio` roles,
 *  `aria-checked` state, roving tab stop and radio-group arrow/Home/End keyboard model without reimplementing
 *  any of them. The `line` variant change presentation only; they retain the same mutually
 *  exclusive selection semantics as the compact field treatment. */
export function Segmented({ options, value, onChange, size = 'md', variant = 'default', className, nowrap = true, 'aria-label': ariaLabel }: {
  options: SegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  /** The track's own height step. `md` is 36 px — the default field beside it — and `sm` 32 px for a
   *  compact group; a coarse pointer raises either to 44 px. It sizes the OUTER track, and the options
   *  fill what its border and padding leave, so the step is never restated on an option or a caller. */
  size?: 'sm' | 'md';
  /** `line` is the quiet horizontal navigation treatment, and the tab strip of a drawer header. */
  variant?: 'default' | 'line';
  className?: string;
  /** Scroll on one line by default. The track scrolls horizontally when it runs out of room, keeps the active
   *  option visible and exposes measured edge state so the stylesheet only paints a fade when content is
   *  genuinely hidden on that side. A fine pointer also gets an arrow on each hidden edge. The `className` of
   *  a `nowrap` track lays out the frame around the track and its arrows, not the track itself. */
  nowrap?: boolean;
  /** Accessible name for the radiogroup — pass it when the control acts as a labelled section nav. */
  'aria-label'?: string;
}) {
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const optionContentKey = options.map((option) => `${option.value}\u0000${option.label}\u0000${option.count ?? ''}`).join('\u0001');
  const orientation = 'horizontal';
  const radioSize = size === 'sm' ? 'sm' : 'default';

  const revealSelected = useCallback((track: HTMLDivElement) => {
    const item = selectedIndex < 0 ? null : itemRefs.current[selectedIndex];
    if (!item || !nowrap) return;
    revealHorizontalItem(track, item);
  }, [nowrap, selectedIndex]);
  const { ref: trackRef, edges: overflowState, measure: measureOverflow } = useHorizontalOverflow<HTMLDivElement>({
    enabled: nowrap,
    onGeometryChange: revealSelected,
  });

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !nowrap || selectedIndex < 0) return;
    revealSelected(track);
    measureOverflow();
  }, [measureOverflow, nowrap, optionContentKey, revealSelected, selectedIndex, trackRef]);

  const onWheel = useCallback((event: globalThis.WheelEvent) => {
    const track = trackRef.current;
    if (!track || !nowrap) return;
    if (consumeHorizontalWheel(track, event)) measureOverflow();
  }, [measureOverflow, nowrap, trackRef]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !nowrap) return;
    // React registers wheel handlers passively at the root. This listener must be explicitly non-passive
    // so vertical page scrolling is blocked only after the horizontal track really moves.
    track.addEventListener('wheel', onWheel, { passive: false });
    return () => track.removeEventListener('wheel', onWheel);
  }, [nowrap, onWheel, trackRef]);

  // An arrow scrolls by most of a page, so the option cut at the edge stays half visible as a cue.
  const scrollTrack = (direction: -1 | 1) => {
    const track = trackRef.current;
    if (!track) return;
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    track.scrollLeft = Math.max(0, Math.min(maxScrollLeft, track.scrollLeft + direction * track.clientWidth * 0.8));
    measureOverflow();
  };

  const edgeStyle = horizontalEdgeFadeStyle(overflowState);

  const strip = (
    // `segmented` / `segmented__option` / `segmented__count` are named for the same reason the toolbar
    // and the field are: this control is one of the handful a DESIGN has to be able to restate, and a
    // stylesheet has nothing to hold on to otherwise. The utilities below stay the built-in reading.
    <RadioGroup
      ref={trackRef}
      value={value}
      onValueChange={onChange}
      aria-label={ariaLabel}
      aria-orientation={orientation}
      variant={variant}
      size={radioSize}
      nowrap={nowrap}
      data-nowrap={nowrap ? 'true' : undefined}
      data-overflow={nowrap ? String(overflowState.overflow) : undefined}
      data-overflow-left={nowrap ? String(overflowState.left) : undefined}
      data-overflow-right={nowrap ? String(overflowState.right) : undefined}
      style={edgeStyle}
      className={cn('segmented', nowrap && 'w-full', !nowrap && className)}
    >
      {options.map((option, index) => {
        const Icon = option.icon;
        return (
          <RadioGroupItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            aria-label={option.count === undefined ? option.label : `${option.label} ${option.count}`}
            appearance="segmented"
            variant={variant}
            size={radioSize}
            ref={(node) => { itemRefs.current[index] = node; }}
            title={option.iconOnly ? option.label : undefined}
            className="duration-[var(--motion-fast)]"
          >
            {Icon ? <Icon aria-hidden className="shrink-0 size-3.25" /> : null}
            {option.iconContent ? <span aria-hidden className="flex shrink-0 items-center">{option.iconContent}</span> : null}
            {/* A whole label remains readable when the option is revealed by swipe or keyboard. */}
            {option.iconOnly ? null : <span title={option.label} className={LABEL_CLASS}>{option.label}</span>}
            {option.count === undefined ? null : (
              <span className={cn('segmented__count text-muted-foreground', variant === 'line' && 'text-meta tabular-nums')} aria-hidden>{option.count}</span>
            )}
          </RadioGroupItem>
        );
      })}
    </RadioGroup>
  );
  if (!nowrap) return strip;
  // Arrows are siblings of the track, never children: a radio group holds only radios. The frame is as wide as
  // its parent allows and never shorter than the track, so a column cannot squeeze the strip and clip its labels.
  return (
    <div className={cn('relative flex w-fit min-h-min min-w-0 max-w-full', className)}>
      {overflowState.left ? <EdgeArrow direction="left" onClick={() => scrollTrack(-1)} /> : null}
      {strip}
      {overflowState.right ? <EdgeArrow direction="right" onClick={() => scrollTrack(1)} /> : null}
    </div>
  );
}
